import type {KpiQualityDataDto, KpiQualityDto} from '@domain/kpis'
import {formatKpiValue} from '@hconnect/common/utils'
import {Column, DataTable} from '@hconnect/uikit/src/lib2'
import {Box, Grid, SxProps, Theme, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'

import {KPIS_WIDGET_NAMESPACE, NO_VALUE_STRING} from '../consts'

type Props = {
  data: KpiQualityDto | undefined
  isVerticalLayout: boolean
  isCompactView: boolean
}

const useQualityColumns = (
  isVerticalLayout: boolean,
  hasNoPyroAndGrindingData: boolean,
  isCompactView: boolean
): Column<KpiQualityDataDto>[] => {
  const {t, i18n} = useTranslation(KPIS_WIDGET_NAMESPACE)
  const language = i18n.language
  const isLargerWidthNeeded = isVerticalLayout && !hasNoPyroAndGrindingData

  const getColumnSx = ({
    largeWidth,
    smallWidth,
    isValueColumn
  }: {
    largeWidth: number
    smallWidth: number
    isValueColumn: boolean
  }): SxProps<Theme> => {
    const whiteSpace = isCompactView ? undefined : isVerticalLayout ? 'pre-line' : 'nowrap'
    const minWidth = isCompactView ? undefined : isLargerWidthNeeded ? largeWidth : smallWidth

    const getPadding = (): SxProps<Theme> => {
      if (isValueColumn && isCompactView) {
        return {pr: {xs: '12px', sm: '12px'}}
      }
      return isVerticalLayout ? {px: 0} : {}
    }

    return {
      ...getPadding(),
      ...(whiteSpace && {whiteSpace}),
      ...(minWidth && {minWidth})
    }
  }

  return [
    {
      key: 'materialName',
      label: t('qualityParameters.label.kpiName'),
      columnSx: getColumnSx({largeWidth: 164, smallWidth: 80, isValueColumn: false}),
      customTemplate: ({materialName}) => materialName ?? t('qualityParameters.label.undefined')
    },
    {
      key: 'limsSamplingPointCode',
      label: t('qualityParameters.label.sampleLocation'),
      columnSx: getColumnSx({largeWidth: 164, smallWidth: 60, isValueColumn: false}),
      customTemplate: ({limsSamplingPointCode}) =>
        limsSamplingPointCode ?? t('qualityParameters.label.undefined')
    },
    {
      key: 'limsMaterialName',
      label: t('qualityParameters.label.productType'),
      columnSx: getColumnSx({largeWidth: 110, smallWidth: 80, isValueColumn: false}),
      customTemplate: ({limsMaterialName}) =>
        limsMaterialName ?? t('qualityParameters.label.undefined')
    },
    {
      key: 'value',
      label: t('qualityParameters.label.actual'),
      align: 'right',
      columnSx: getColumnSx({largeWidth: 110, smallWidth: 60, isValueColumn: true}),
      customTemplate: ({value, unit}) => {
        const formattedValue = formatKpiValue(value?.toString(), language)
        return (
          <span>
            {formattedValue ?? NO_VALUE_STRING}
            {formattedValue && unit && ` ${unit}`}
          </span>
        )
      }
    }
  ]
}

export const QualityParametersTable = ({data, isVerticalLayout, isCompactView}: Props) => {
  const {t} = useTranslation(KPIS_WIDGET_NAMESPACE)
  const noPyroAndGrindingData = !data?.pyro.length && !data?.grinding.length
  const pyroColumns = useQualityColumns(isVerticalLayout, noPyroAndGrindingData, isCompactView)
  const grindingColumns = useQualityColumns(isVerticalLayout, noPyroAndGrindingData, isCompactView)

  const configs = [
    {
      key: 'pyro',
      title: t('qualityParameters.label.pyro'),
      columns: pyroColumns,
      data: data?.pyro ?? [],
      testId: 'quality-parameters-pyro-table'
    },
    {
      key: 'grinding',
      title: t('qualityParameters.label.grinding'),
      columns: grindingColumns,
      data: data?.grinding ?? [],
      testId: 'quality-parameters-grinding-table'
    }
  ]

  if (isVerticalLayout) {
    return (
      <Grid container direction="column" spacing={2} sx={{height: 'auto'}}>
        {configs.map(({key, title, columns, data, testId}) => {
          const hasData = data.length > 0

          // Table with data → fill remaining space
          // Table without data → minimal height
          const flexValue = hasData ? 1 : 0
          const minHeight = 128

          return (
            <Grid
              key={key}
              item
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: flexValue,
                minHeight,
                overflow: 'hidden'
              }}
            >
              <Box sx={{mb: 2}}>
                <Typography variant="subtitle1" fontWeight="medium">
                  {title}
                </Typography>
              </Box>
              <DataTable<KpiQualityDataDto>
                columns={columns}
                data={data}
                keyExtractor={(item) => item.value?.toString() ?? NO_VALUE_STRING}
                data-test-id={testId}
                tableContainerSx={{
                  height: '100%',
                  overflowY: hasData ? 'auto' : 'hidden',
                  overflowX: 'auto',
                  p: 0
                }}
              />
            </Grid>
          )
        })}
      </Grid>
    )
  }

  // Horizontal layout
  return (
    <Grid container spacing={2}>
      {configs.map(({key, title, columns, data, testId}) => (
        <Grid key={key} item xs={12} sm={6}>
          <Box sx={{mb: 1}}>
            <Typography variant="subtitle1" fontWeight="medium">
              {title}
            </Typography>
          </Box>
          <DataTable<KpiQualityDataDto>
            columns={columns}
            data={data}
            keyExtractor={(item) => item.value?.toString() ?? NO_VALUE_STRING}
            data-test-id={testId}
            tableContainerSx={{
              overflowX: 'auto'
            }}
          />
        </Grid>
      ))}
    </Grid>
  )
}
